import { useCallback, useEffect, useState } from "react";
import { Badge, Button, Card, IndexTable, Text, TextField } from "@shopify/polaris";
import { useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import agent from "../../app/api/agent";
import { formatLocalDateTime } from "./utils/dateTime";
import { AdminFitmentImportLog, AdminPagedResult } from "../../app/models/Admin";

type AdminConsoleContext = {
  token: string;
  onUnauthorized: () => void;
};

const pageSize = 50;

export default function AdminFitmentImportLogsPage() {
  const { token, onUnauthorized } = useOutletContext<AdminConsoleContext>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [result, setResult] = useState<AdminPagedResult<AdminFitmentImportLog> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const page = Math.max(1, Number(searchParams.get("page") || "1") || 1);
  const shopIdValue = searchParams.get("shopId") || "";
  const state = searchParams.get("state") || "";

  const loadLogs = useCallback(async () => {
    setLoading(true);
    setError(null);

    const parsedShopId = shopIdValue ? Number(shopIdValue) : undefined;

    try {
      const response = await agent.Admin.fitmentImportLogs(
        token,
        page,
        pageSize,
        Number.isFinite(parsedShopId) ? parsedShopId : undefined,
        state || undefined
      );
      setResult(response);
    } catch {
      setError("Unable to load fitment import logs.");
      onUnauthorized();
    } finally {
      setLoading(false);
    }
  }, [token, page, shopIdValue, state, onUnauthorized]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const totalPages = result ? Math.max(1, Math.ceil(result.totalCount / result.pageSize)) : 1;

  const setPage = (nextPage: number) => {
    const next = new URLSearchParams(searchParams);
    next.set("page", String(nextPage));
    setSearchParams(next);
  };

  const updateFilter = (updates: Record<string, string>) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (!value) {
        next.delete(key);
      } else {
        next.set(key, value);
      }
    });
    next.set("page", "1");
    setSearchParams(next);
  };

  const renderState = (value?: string) => {
    if (!value) return <Text as="span" tone="subdued">-</Text>;

    const normalized = value.toLowerCase();
    if (normalized.includes("complete") || normalized.includes("success")) return <Badge tone="success">{value}</Badge>;
    if (normalized.includes("fail") || normalized.includes("error")) return <Badge tone="critical">{value}</Badge>;

    return <Badge tone="attention">{value}</Badge>;
  };

  return (
    <Card>
      <div style={{ display: "grid", gap: "0.75rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Text as="h3" variant="headingMd">Fitment import logs</Text>
          <Button onClick={loadLogs} loading={loading}>Refresh logs</Button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
          <TextField
            label="Filter by Shop ID"
            value={shopIdValue}
            onChange={(value) => updateFilter({ shopId: value })}
            autoComplete="off"
            placeholder="123"
          />
          <TextField
            label="Filter by state"
            value={state}
            onChange={(value) => updateFilter({ state: value })}
            autoComplete="off"
            placeholder="Completed"
          />
        </div>
        {error ? <Text as="p" tone="critical">{error}</Text> : null}
        {loading ? (
          <Text as="p">Loading fitment import logs...</Text>
        ) : !result || result.items.length === 0 ? (
          <Text as="p">No fitment import logs found.</Text>
        ) : (
          <IndexTable
            resourceName={{ singular: "fitment import log", plural: "fitment import logs" }}
            itemCount={result.items.length}
            selectable={false}
            headings={[
              { title: "Queue" },
              { title: "Store" },
              { title: "File" },
              { title: "Format" },
              { title: "State" },
              { title: "Added" },
              { title: "Started" },
              { title: "Completed" },
              { title: "Counts" },
              { title: "Error" }
            ]}
          >
            {result.items.map((row, index) => (
              <IndexTable.Row id={`${row.queueId}`} key={row.queueId} position={index}>
                <IndexTable.Cell>{row.queueId}</IndexTable.Cell>
                <IndexTable.Cell>
                  <Button variant="plain" onClick={() => navigate(`/pf-console/stores/${row.shopId}`)}>{row.shopName || row.shopDomain || `Shop ${row.shopId}`}</Button>
                </IndexTable.Cell>
                <IndexTable.Cell>{row.originalFileName || row.storedFileName || "-"}</IndexTable.Cell>
                <IndexTable.Cell>{row.importFormat || "-"}</IndexTable.Cell>
                <IndexTable.Cell>{renderState(row.state)}</IndexTable.Cell>
                <IndexTable.Cell>{formatLocalDateTime(row.addedOn)}</IndexTable.Cell>
                <IndexTable.Cell>{formatLocalDateTime(row.startedAt)}</IndexTable.Cell>
                <IndexTable.Cell>{formatLocalDateTime(row.completedAt)}</IndexTable.Cell>
                <IndexTable.Cell>{`Declared: ${row.declaredRecordCount ?? 0}, Parsed: ${row.parsedRecordCount ?? 0}, Applied: ${row.appliedRecordCount ?? 0}`}</IndexTable.Cell>
                <IndexTable.Cell>{row.error || "-"}</IndexTable.Cell>
              </IndexTable.Row>
            ))}
          </IndexTable>
        )}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Button disabled={page <= 1 || loading} onClick={() => setPage(page - 1)}>Previous</Button>
          <Text as="span">Page {page} of {totalPages}</Text>
          <Button disabled={page >= totalPages || loading} onClick={() => setPage(page + 1)}>Next</Button>
        </div>
      </div>
    </Card>
  );
}
